import * as React from 'react';

import { apiFetch } from '../../api/httpClient';

export async function subGet<T = unknown>(path: string): Promise<T[]> {
    const res = await apiFetch(path, { method: 'GET' });
    if (!res.ok) return [];
    const json = (await res.json().catch(() => [])) as unknown;
    return Array.isArray(json) ? (json as T[]) : [];
}

export async function subGetOne<T = Record<string, unknown>>(path: string): Promise<T | null> {
    const res = await apiFetch(path, { method: 'GET' });
    if (!res.ok) return null;
    return (await res.json().catch(() => null)) as T | null;
}

export async function subSend(
    path: string,
    method: 'POST' | 'PUT' | 'DELETE',
    body?: unknown
): Promise<{ ok: boolean; status: number; json: unknown }> {
    const res = await apiFetch(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, json };
}

export type UserOption = { id: string; name: string };

/** Fetches the tenant users once for assignee / owner pickers in the custom sub-resource tabs. */
export function useUserOptions(): UserOption[] {
    const [options, setOptions] = React.useState<UserOption[]>([]);
    React.useEffect(() => {
        let alive = true;
        void (async () => {
            const rows = await subGet<Record<string, unknown>>('/api/users');
            if (!alive) return;
            setOptions(
                rows
                    .map(u => ({
                        id: String(u.id ?? u.Id ?? ''),
                        name: String(u.fullName ?? u.FullName ?? u.username ?? u.Username ?? u.id ?? ''),
                    }))
                    .filter(u => u.id)
            );
        })();
        return () => {
            alive = false;
        };
    }, []);
    return options;
}

/** Reload counter hook — bump to refetch a sub-resource list. */
export function useReloadKey(): [number, () => void] {
    const [key, setKey] = React.useState(0);
    const reload = React.useCallback(() => setKey(k => k + 1), []);
    return [key, reload];
}
